import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import ProjectVisualizer from "@/components/ProjectVisualizer";
import ARCameraView from "@/components/ARCameraView";
import ChatPanel from "@/components/ChatPanel";
import ProjectEditModal from "@/components/ProjectEditModal";
import VersionHistory from "@/components/VersionHistory";
import GanttTimeline from "@/components/GanttTimeline";
import ExecutiveSummary from "@/components/ExecutiveSummary";
import RegulatoryPanel from "@/components/RegulatoryPanel";
import { CostBreakdown, RiskPanel, MilestoneTimeline } from "@/components/AnalysisPanels";
import ScopeEditor from "@/components/ScopeEditor";
import ContractModal from "@/components/ContractGenerator";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import ApplicationPanel from "@/components/ApplicationPanel";
import ChangeRequestPanel from "@/components/ChangeRequestPanel";
import GuestCTA from "@/components/GuestCTA";
import VisibilityPanel from "@/components/VisibilityPanel";
import ProjectDescription from "@/components/ProjectDescription";
import ProjectPreview2D from "@/components/ProjectPreview2D";
import {
  ArrowLeft, Clock, Cpu, Layers, Calculator,
  Send, Camera, CheckCircle, AlertCircle, Loader2, Pencil, Trash2, FileDown, FileText,
  Globe2, Lock, Users, MonitorSmartphone
} from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const complexityColor = {
  "Niedrig": "#00F0FF",
  "Mittel": "#FFB800",
  "Hoch": "#FF3B30",
  "Sehr Hoch": "#FF3B30",
};

const statusConfig = {
  open: { label: "Offen", color: "#00F0FF", bg: "#00F0FF" },
  applications: { label: "Bewerbungen", color: "#FFB800", bg: "#FFB800" },
  assigned: { label: "Vergeben", color: "#A855F7", bg: "#A855F7" },
  completed: { label: "Abgeschlossen", color: "#10B981", bg: "#10B981" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const role = user?.role || "client";
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hourlyRate, setHourlyRate] = useState("");
  const [showAR, setShowAR] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [offerSent, setOfferSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUiPreview, setShowUiPreview] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
      setProject(res.data);
      setOfferSent(res.data.offerSent);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    fetchProject().finally(() => setLoading(false));
  }, [fetchProject]);

  const analysis = project?.analysis;
  const avgHours = analysis ? (analysis.estimatedHours_min + analysis.estimatedHours_max) / 2 : 0;
  const totalPrice = hourlyRate ? (parseFloat(hourlyRate) * avgHours) : 0;

  const [showContract, setShowContract] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfBody, setPdfBody] = useState(null);

  const handleSendOffer = async () => {
    if (!hourlyRate || totalPrice <= 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/projects/${id}/offer`, { hourlyRate: parseFloat(hourlyRate), totalPrice }, { withCredentials: true });
      setOfferSent(true);
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  const handleSaveProject = async (formData) => {
    try {
      const res = await axios.put(`${API}/projects/${id}`, formData, { withCredentials: true });
      setProject(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleReanalyze = async () => {
    try {
      const res = await axios.post(`${API}/projects/${id}/reanalyze`, { outputLanguage: lang }, { withCredentials: true });
      setProject(res.data);
      setOfferSent(false);
      setShowEdit(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Projekt wirklich loeschen? Diese Aktion kann nicht rueckgaengig gemacht werden.")) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
    }
    setDeleting(false);
  };

  const handleExportPDF = () => {
    // Capture a wide, fit-to-all snapshot of the 3D preview so the PDF shows every component.
    let preview3dImage = null;
    try {
      const wrap = document.querySelector('[data-testid="project-3d-canvas"]');
      // Prefer the snapshot helper exposed by ProjectVisualizer (renders at 1920x880 with
      // bounding-box-fit camera). Falls back to the raw canvas pixels if not available yet.
      if (wrap && typeof wrap.__radarSnapshot__ === "function") {
        preview3dImage = wrap.__radarSnapshot__(1920, 880);
      } else {
        const canvas = wrap?.querySelector("canvas");
        if (canvas) preview3dImage = canvas.toDataURL("image/png");
      }
    } catch (err) {
      console.warn("3D snapshot failed:", err);
    }
    setPdfBody({ preview3dImage });
    setShowPdfPreview(true);
  };

  const handleVersionRestore = async () => {
    const res = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
    setProject(res.data);
    setOfferSent(res.data.offerSent);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="glass-surface rounded-lg p-12 text-center">
          <AlertCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
          <h2 className="font-['Outfit'] text-xl text-white mb-2">Projekt nicht gefunden</h2>
          <Link to="/dashboard" className="text-[#00F0FF] text-sm hover:underline">Zurueck zum Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="project-detail-page" className="min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/dashboard" data-testid="back-to-dashboard-link" className="inline-flex items-center gap-2 text-[#A0A0B0] hover:text-[#00F0FF] transition-colors mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Zurueck zum Dashboard
        </Link>

        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.2em] uppercase">{project.projectCategory}</span>
              {(() => {
                const s = statusConfig[project.status || "open"];
                return (
                  <span
                    data-testid="project-status-badge"
                    className="inline-flex items-center gap-1.5 text-[10px] font-['JetBrains_Mono'] tracking-widest uppercase px-2.5 py-1 rounded-full border"
                    style={{ color: s.color, borderColor: `${s.color}40`, background: `${s.bg}15` }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                    {s.label}
                  </span>
                );
              })()}
              {project.assigned_freelancer_name && (
                <span className="text-[10px] font-['JetBrains_Mono'] text-[#A0A0B0]">
                  → {project.assigned_freelancer_name}
                </span>
              )}
              {/* Visibility badge — visible to everyone who can see the project */}
              {(() => {
                const isPub = !!project.isPublic;
                const hasInvites = (project.invited_freelancer_ids || []).length > 0;
                const cfg = isPub
                  ? { label: "Öffentlich", color: "#00E5A0", Icon: Globe2 }
                  : hasInvites
                  ? { label: "Bilateral", color: "#FFB800", Icon: Users }
                  : { label: "Privat", color: "#909098", Icon: Lock };
                return (
                  <span
                    data-testid="visibility-badge"
                    className="inline-flex items-center gap-1.5 text-[10px] font-['JetBrains_Mono'] tracking-widest uppercase px-2.5 py-1 rounded-full border"
                    style={{ color: cfg.color, borderColor: `${cfg.color}40`, background: `${cfg.color}15` }}
                  >
                    <cfg.Icon className="w-3 h-3" />
                    {cfg.label}
                  </span>
                );
              })()}
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light tracking-tight text-white mt-2">{project.projectTitle}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button
              data-testid="contract-header-btn"
              onClick={() => setShowContract(true)}
              className="btn-primary flex items-center gap-2 text-sm py-2 px-4"
            >
              <FileText className="w-4 h-4" /> Vertrag erstellen
            </button>
            <button
              data-testid="export-pdf-btn"
              onClick={handleExportPDF}
              className="btn-secondary flex items-center gap-2 text-sm py-2 px-3"
              title="Als PDF exportieren"
            >
              <FileDown className="w-4 h-4" /> PDF
            </button>
            {user && (
              <>
                <button
                  data-testid="edit-project-btn"
                  onClick={() => setShowEdit(true)}
                  className="btn-secondary flex items-center gap-2 text-sm py-2 px-3"
                >
                  <Pencil className="w-4 h-4" /> Bearbeiten
                </button>
                <button
                  data-testid="delete-project-btn"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 text-sm py-2 px-3 bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-all font-['Outfit'] font-medium"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Visibility Panel — owner only */}
        {user && project.user_id === user.user_id && (
          <div className="mb-6">
            <VisibilityPanel project={project} onUpdated={fetchProject} />
          </div>
        )}

        {/* 1. Project Description — full-width, structured renderer */}
        <div className="mb-6">
          <ProjectDescription
            description={project.projectDescription}
            category={project.projectCategory}
            title={project.projectTitle}
            index={1}
            showEditButton={!!user && project.user_id === user.user_id}
            onEdit={() => setShowEdit(true)}
          />
        </div>

        {/* Executive Summary (top, full width) */}
        {analysis?.executiveSummary && (
          <div className="mb-6">
            <ExecutiveSummary
              summary={analysis.executiveSummary}
              complexity={analysis.projectComplexity}
              hoursMin={analysis.estimatedHours_min}
              hoursMax={analysis.estimatedHours_max}
              totalWeeks={(analysis.milestones || []).reduce((s, m) => s + (m.weeks || 0), 0)}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* 3D Viewer */}
          <div className="lg:col-span-8 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-['Outfit'] text-lg font-medium text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-[#00F0FF]" /> 3D-Projektvorschau
              </h2>
              <button
                data-testid="ar-view-btn"
                onClick={() => setShowAR(true)}
                className="btn-secondary text-sm flex items-center gap-2 py-2 px-4"
              >
                <Camera className="w-4 h-4" /> AR-Kamera
              </button>
            </div>
            <ProjectVisualizer visualElements={analysis?.visualElements || []} analysis={analysis} />

            {/* Cost Breakdown */}
            <CostBreakdown analysis={analysis} hourlyRate={hourlyRate} />

            {/* Risk Analysis */}
            <RiskPanel analysis={analysis} />

            {/* Regulatorischer Aufwand & Audit-Puffer (only for regulated sectors) */}
            {analysis?.regulatory?.is_regulated && (
              <RegulatoryPanel regulatory={analysis.regulatory} />
            )}

            {/* Milestone Timeline */}
            <MilestoneTimeline analysis={analysis} />

            {/* Visual Gantt Timeline */}
            <GanttTimeline
              milestones={analysis?.milestones || []}
              startDate={project?.createdAt}
              totalHoursMin={analysis?.estimatedHours_min}
              totalHoursMax={analysis?.estimatedHours_max}
            />

            {/* Interactive Scope Editor */}
            <ScopeEditor analysis={analysis} hourlyRate={hourlyRate || "85"} />

            {/* UI-Vorschau (Prototyp) — opt-in, zero-cost CSS wireframe */}
            {analysis?.visualElements?.length > 0 && (
              <section data-testid="ui-preview-section" className="space-y-3">
                {!showUiPreview ? (
                  <button
                    type="button"
                    data-testid="ui-preview-toggle-btn"
                    onClick={() => setShowUiPreview(true)}
                    className="w-full glass-surface rounded-xl border border-white/8 hover:border-[#00F0FF]/40 transition-all duration-300 p-5 flex items-center gap-4 group cursor-pointer text-left"
                  >
                    <span className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center text-[#00F0FF] group-hover:bg-[#00F0FF]/25 transition-colors">
                      <MonitorSmartphone className="w-5 h-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-['Outfit'] text-base text-white font-medium">
                        {t.preview2d?.cta_show || "UI-Vorschau ansehen (erste Inspiration)"}
                      </div>
                      <div className="text-xs text-[#909098] font-['JetBrains_Mono'] mt-0.5">
                        {t.preview2d?.subtitle || "Zero-Cost CSS-Wireframe — keine KI-Generierung"}
                      </div>
                    </div>
                    <span className="text-[#00F0FF] text-sm font-['Outfit'] flex items-center gap-1 group-hover:gap-2 transition-all">
                      {t.preview2d?.cta_open || "Öffnen"} →
                    </span>
                  </button>
                ) : (
                  <section className="glass-surface rounded-2xl p-6 sm:p-8 border border-white/8">
                    <header className="flex items-start justify-between gap-3 flex-wrap mb-5 pb-4 border-b border-white/5">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center text-[#00F0FF]">
                          <MonitorSmartphone className="w-4 h-4" />
                        </span>
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] font-['JetBrains_Mono']">
                              {t.preview2d?.title || "UI-Vorschau"}
                            </span>
                          </div>
                          <h2 className="font-['Outfit'] text-xl sm:text-2xl text-white font-light tracking-tight">
                            {t.preview2d?.title || "UI-Vorschau"}
                          </h2>
                          <p className="text-[12px] text-[#909098] font-['JetBrains_Mono'] mt-0.5">
                            {t.preview2d?.subtitle || "Zero-Cost CSS-Wireframe — keine KI-Generierung"}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="ui-preview-hide-btn"
                        onClick={() => setShowUiPreview(false)}
                        className="text-xs text-[#909098] hover:text-white font-['Outfit'] px-3 py-1.5 rounded-md border border-white/10 hover:border-white/30 transition-colors"
                      >
                        {t.preview2d?.cta_hide || "Ausblenden"}
                      </button>
                    </header>
                    <ProjectPreview2D
                      project={project}
                      variant="landing"
                      labels={t.preview2d}
                      subtitle={t.preview2d?.subtitle}
                      disclaimerHtml={t.preview2d?.disclaimer}
                    />
                  </section>
                )}
              </section>
            )}

            {/* AI Badge */}
            <div className="flex items-center gap-2 text-xs text-[#606070] font-['JetBrains_Mono']">
              <Cpu className="w-3 h-3 text-[#00F0FF]" />
              Analyse erstellt mit GPT-5.2
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* Analysis Dashboard */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="glass-surface rounded-lg p-6">
              <h3 className="font-['Outfit'] text-sm font-medium text-[#A0A0B0] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-[#00F0FF]" /> Analyse-Dashboard
              </h3>
              <div className="space-y-4">
                <div>
                  <span className="text-xs text-[#606070] font-['JetBrains_Mono']">Komplexitaet</span>
                  <div className="mt-1">
                    <span data-testid="project-complexity" className="text-lg font-['Outfit'] font-semibold" style={{ color: complexityColor[analysis?.projectComplexity] || "#fff" }}>
                      {analysis?.projectComplexity}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-[#606070] font-['JetBrains_Mono']">Geschaetzter Aufwand</span>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <Clock className="w-4 h-4 text-[#00F0FF]" />
                    <span data-testid="estimated-hours" className="text-lg font-['JetBrains_Mono'] text-white">
                      {analysis?.estimatedHours_min} – {analysis?.estimatedHours_max}
                    </span>
                    <span className="text-sm text-[#606070]">Stunden</span>
                  </div>
                  <div className="mt-1 ml-6 text-xs text-[#A0A0B0] font-['JetBrains_Mono']" data-testid="estimated-days">
                    ≈ {Math.round((analysis?.estimatedHours_min || 0) / 8)}–{Math.round((analysis?.estimatedHours_max || 0) / 8)} Tage
                    <span className="text-[#606070] ml-1.5">(8 h/Tag)</span>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-[#606070] font-['JetBrains_Mono']">Empfohlener Tech-Stack</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {analysis?.techStack_reco.map((tech, i) => (
                      <span key={i} data-testid={`tech-stack-item-${i}`} className="text-xs px-3 py-1.5 bg-[#00F0FF]/5 border border-[#00F0FF]/20 text-[#00F0FF] font-['JetBrains_Mono']">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="pt-3 border-t border-white/5">
                  <span className="text-xs text-[#606070] font-['JetBrains_Mono']">Visuelle Elemente</span>
                  <div className="mt-1 flex items-center gap-4">
                    <span className="text-sm text-[#A0A0B0]">
                      <span className="text-white font-['JetBrains_Mono']">{analysis?.visualElements.filter(e => e.type === "Plane").length}</span> Screens
                    </span>
                    <span className="text-sm text-[#A0A0B0]">
                      <span className="text-white font-['JetBrains_Mono']">{analysis?.visualElements.filter(e => e.type === "Cube").length}</span> Funktionen
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Version History - registered users only */}
            {user && <VersionHistory projectId={id} currentVersion={project.version || 1} onRestore={handleVersionRestore} />}

            {/* Guest CTA — encourages signup so they can interact */}
            {!user && (
              <GuestCTA
                testId="project-detail-guest-cta"
                message="Du befindest dich im Vorschau-Modus. Erstelle ein kostenloses Konto, um mit dem Auftraggeber zu chatten, dich zu bewerben oder ein eigenes Projekt einzustellen."
              />
            )}

            {/* Collaboration: Applications - registered only */}
            {user && <ApplicationPanel projectId={id} projectOwnerId={project.user_id} />}

            {/* Collaboration: Change Requests - registered only */}
            {user && <ChangeRequestPanel projectId={id} />}

            {/* Calculator - Freelancer only, registered */}
            {user && role === "freelancer" && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="glass-surface rounded-lg p-6">
                <h3 className="font-['Outfit'] text-sm font-medium text-[#A0A0B0] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-[#00F0FF]" /> Kalkulator
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-[#606070] font-['JetBrains_Mono'] block mb-2">Dein Stundensatz (EUR)</label>
                    <input
                      data-testid="hourly-rate-input"
                      type="number"
                      min="0"
                      step="5"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      placeholder="z.B. 85"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 font-['JetBrains_Mono'] text-sm transition-colors outline-none"
                    />
                  </div>

                  {hourlyRate && parseFloat(hourlyRate) > 0 && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="pt-3 border-t border-white/5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-[#606070]">Mittelwert Stunden</span>
                        <span className="text-sm font-['JetBrains_Mono'] text-[#A0A0B0]">
                          {avgHours}h <span className="text-[#606070]">≈ {Math.round(avgHours / 8)} T.</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-[#606070]">Stundensatz</span>
                        <span className="text-sm font-['JetBrains_Mono'] text-[#A0A0B0]">{parseFloat(hourlyRate).toFixed(2)} EUR</span>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-[#00F0FF]/20 mt-3">
                        <span className="text-sm font-['Outfit'] font-medium text-white">Gesamtpreis</span>
                        <span data-testid="total-price" className="text-xl font-['JetBrains_Mono'] font-semibold neon-text">
                          {totalPrice.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR
                        </span>
                      </div>
                    </motion.div>
                  )}

                  {offerSent ? (
                    <div data-testid="offer-sent-confirmation" className="flex items-center gap-2 p-3 bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded">
                      <CheckCircle className="w-5 h-5 text-[#00F0FF]" />
                      <span className="text-sm text-[#00F0FF] font-['Outfit']">Angebot erfolgreich versendet!</span>
                    </div>
                  ) : (
                    <button
                      data-testid="send-offer-btn"
                      onClick={handleSendOffer}
                      disabled={!hourlyRate || totalPrice <= 0 || sending}
                      className={`w-full flex items-center justify-center gap-2 py-3 px-4 font-['Outfit'] font-medium transition-all duration-300 ${
                        !hourlyRate || totalPrice <= 0 ? "bg-white/5 text-[#606070] border border-white/5 cursor-not-allowed" : "btn-primary"
                      }`}
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Angebot basierend auf Analyse senden
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {role === "client" && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="glass-surface rounded-lg p-6 text-center">
                <AlertCircle className="w-8 h-8 text-[#FFB800] mx-auto mb-3" />
                <p className="text-sm text-[#A0A0B0]">
                  Wechsle zur <span className="text-[#FFB800] font-medium">Auftragnehmer</span>-Rolle, um den Kalkulator und die Angebotsfunktion zu nutzen.
                </p>
              </motion.div>
            )}

            {/* Chat Panel - In sidebar - registered users only */}
            {user && <ChatPanel projectId={id} isOpen={chatOpen} onClose={() => setChatOpen(false)} />}
          </div>
        </div>
      </div>

      {/* Contract Generator Modal */}
      {showContract && project && (
        <ContractModal project={project} analysis={analysis} hourlyRate={hourlyRate || "85"} onClose={() => setShowContract(false)} />
      )}

      {/* PDF Preview Modal */}
      {showPdfPreview && project && (
        <PdfPreviewModal
          title="Projekt-Export"
          subtitle={project.projectTitle}
          filename={`${project.projectTitle.replace(/ /g, "_")}.pdf`}
          url={`${API}/projects/${id}/pdf`}
          method="post"
          postBody={pdfBody || {}}
          onClose={() => { setShowPdfPreview(false); setPdfBody(null); }}
        />
      )}

      {/* Edit Modal */}
      {showEdit && project && (
        <ProjectEditModal
          project={project}
          onClose={() => setShowEdit(false)}
          onSave={handleSaveProject}
          onReanalyze={handleReanalyze}
        />
      )}

      {/* AR Camera View */}
      <ARCameraView isOpen={showAR} onClose={() => setShowAR(false)} visualElements={analysis?.visualElements || []} />
    </div>
  );
}
